import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { EnvelopeSimpleIcon, PaperclipIcon } from '@phosphor-icons/react';
import { FullMessageDialog } from '@/components/email-viewer/full-message-dialog';
import type { AttachedChildEmail } from '@erp/rpc';

interface Props {
  emails: AttachedChildEmail[];
}

/**
 * The emails forwarded ONTO the one being reviewed, each openable.
 *
 * Shared by the labeling and review pages on purpose. Outlook's "forward as
 * attachment" arrives as a Graph `itemAttachment` and ingestion files it as its own
 * inbound email with no mailbox of its own — so it is not in the attachment list, and
 * the mailbox-scoped queues can never list it as a candidate. Nesting it under the
 * parent is the ONLY way it is reachable, which makes this section the fix for what
 * users report as "the email attachment did not come through". Two copies of that
 * would be two chances to quietly drop one.
 *
 * No attachment roles here, unlike an attachment row: the role question is "did this
 * part carry the supplier's accept/reject lines", and it is answered about things
 * that get PARSED. A forwarded message is read by a human; its own decision sheet, if
 * it has one, is a file hanging off it.
 */
export function ForwardedEmailsSection({ emails }: Props) {
  const gt = useGT();
  const [openMessageId, setOpenMessageId] = useState<string | null>(null);

  if (emails.length === 0) return null;

  return (
    <section className="space-y-1.5">
      <div className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        <T context="849 section heading">Forwarded emails</T>
      </div>
      {/* Says what these are and why they look missing. Someone forwarded a message
          AS AN ATTACHMENT; ingestion files it as its own email, so it is not in the
          attachment list above and the reviewer was told it "did not come through". */}
      <p className="text-xs text-muted-foreground">
        <T context="849 forwarded email hint">
          Sent as attached messages. They are stored separately, so they do not
          appear under Attachments.
        </T>
      </p>
      <div className="space-y-1.5">
        {emails.map((email) => (
          <button
            key={email.inboundEmailId}
            type="button"
            onClick={() => setOpenMessageId(email.inboundEmailId)}
            className="flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left hover:bg-muted/50"
          >
            <EnvelopeSimpleIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm">
                {email.subject ??
                  gt('(no subject)', { context: '849 email missing subject' })}
              </span>
              <span className="block truncate font-mono text-xs text-muted-foreground">
                {email.from}
              </span>
            </span>
            {email.attachmentCount > 0 ? (
              <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                <PaperclipIcon className="h-3 w-3" />
                {email.attachmentCount}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <FullMessageDialog
        inboundEmailId={openMessageId}
        onClose={() => setOpenMessageId(null)}
      />
    </section>
  );
}
